import { COOKIES } from '@/utils/cookies';
import { getCookieValue } from '@/utils/cookies';
import { getHeaderValue } from '@/utils/cookies/server';
import React, { cache } from 'react';
import type { Region } from '@/types/constants';
import { BRAND, COUNTRY, DEVICE, PRODUCT, REGIONS } from '@/utils/constants';
import { getExperiments } from '@/logic/experiments';
import { getFeatureFlags } from '@/logic/page-config/feature-flags';
import { POKER_DESKTOP_CLIENT_HEADER } from '@/middleware/poker-access-middleware';
import type { Experiment } from '@/types/aggregation/home';
import { AMPLITUDE_EXPERIMENTS, getExperimentValue, isExperimentEnabled } from '@/utils/experiments';
import type { LauncherUrlResolutionContext } from '@/utils/url/launcher-url';
import { GAME_PLAY_PATH_PREFIX } from '@/utils/url/game-play-path';
import { GAME_WINDOW_PLAY_PATH_PREFIX } from '@/utils/url/game-window-play-path';

const IOS_MOBILE_AGENTS = /iphone|ipod|ipad/i;

export type ServerContext = {
    platform: string; // TODO: remove this and migrate to booleans?
    isDesktop: boolean;
    isMobile: boolean;
    isTablet: boolean;
    isAndroid: boolean;
    isIos: boolean;
    isAndroidWrapper: boolean;
    isIosWrapper: boolean;
    isXsellWrapper: boolean;
    isPokerDesktopClient: boolean;
    isPokerWrapper: boolean;

    region: Region;
    isLoggedIn: boolean;
    currentRoute: string;

    featureFlags: Record<string, true>;
    experiments: Experiment;
    launcherUrlResolution: LauncherUrlResolutionContext;
    isSiteWideJackpotEnabled: boolean;
    hideSwjTickers: boolean;
    swjTickerRedesignVariant: string | undefined;
};

// Warning: this becomes a problem when a server component becomes client component
// react cache can only be used in server components
// and a server component that becomes a client component will read the initial state
export const getServerContext = cache((): ServerContext => {
    if (Boolean(React.useState) || typeof window !== 'undefined') {
        // TODO: temporarily disabled
        // throw new Error('Server context can only be used in server components');
    }

    return {
        platform: '',
        isDesktop: false,
        isMobile: false,
        isTablet: false,
        isAndroid: false,
        isIos: false,
        isAndroidWrapper: false,
        isIosWrapper: false,
        isXsellWrapper: false,
        isPokerDesktopClient: false,
        isPokerWrapper: false,

        region: REGIONS.MI as Region,
        isLoggedIn: false,
        currentRoute: '',

        featureFlags: {} as Record<string, true>,
        experiments: {} as Experiment,
        launcherUrlResolution: {
            shouldUseSameDomainLauncher: false,
            isGameWindowV3Enabled: false,
            gamePlayPath: GAME_PLAY_PATH_PREFIX
        },
        isSiteWideJackpotEnabled: false,
        hideSwjTickers: false,
        swjTickerRedesignVariant: undefined
    };
});

// TODO: check if static pages initialize server context
export const initializeServerContext = cache(async (currentRoute: string) => {
    if (Boolean(React.useState) || typeof window !== 'undefined') {
        throw new Error('Server context can only be used in server components');
    }

    const serverContext = getServerContext();

    const platform = await getCookieValue(COOKIES.PLATFORM);
    serverContext.platform = platform;
    serverContext.isDesktop = platform === DEVICE.DESKTOP;
    serverContext.isMobile = platform === DEVICE.MOBILE || platform === DEVICE.MOBILE_WEB;
    // TODO: change to a user agent check.
    serverContext.isTablet = platform === DEVICE.TABLET;

    // this will match both browser and app webview
    const userAgent = await getHeaderValue('User-Agent');

    if (userAgent) {
        serverContext.isAndroid = userAgent.toLowerCase().includes('android');
        serverContext.isIos = IOS_MOBILE_AGENTS.test(userAgent);
    } else {
        serverContext.isAndroid = false;
        serverContext.isIos = false;
    }

    // FDPoker DLC webview is detected from the request User-Agent (the same signal the
    // poker access middleware uses), so the flag is available during SSR like the other
    // wrapper checks.
    serverContext.isPokerWrapper = Boolean(userAgent && userAgent.includes('FDPoker'));

    const wrapper = (await getCookieValue(COOKIES.WRAPPER)).toLowerCase();
    serverContext.isAndroidWrapper = wrapper === 'android';
    serverContext.isIosWrapper = wrapper === 'ios';
    serverContext.isXsellWrapper = process.env.PRODUCT === PRODUCT.XSELL;

    if (process.env.PRODUCT === PRODUCT.POKER) {
        // Lazy-loaded so the static `next/headers` import is not part of this
        // module's surface — `collection-zone.tsx` reaches this file through a
        // `dynamic(..., { ssr: false })` chunk inside a `'use client'` parent,
        // and Next.js forbids `next/headers` in any client bundle.
        const { headers } = await import('next/headers');
        const headerList = await headers();
        serverContext.isPokerDesktopClient = headerList.get(POKER_DESKTOP_CLIENT_HEADER) === 'true';
    }

    serverContext.currentRoute = currentRoute || '/';
    let region = (await getCookieValue(COOKIES.REGION)) as Region;
    if (!region) {
        if (process.env.BRAND === BRAND.MOHEGANSUN) {
            region = REGIONS.CT;
        } else if (process.env.COUNTRY === COUNTRY.USA) {
            region = REGIONS.MI;
        } else {
            region = REGIONS.ON;
        }
    }
    serverContext.region = region.toUpperCase() as Region;
    const [authToken, loginToken] = await Promise.all([
        getCookieValue(COOKIES.AUTH_TOKEN),
        getCookieValue(COOKIES.LOGIN_TOKEN)
    ]);
    serverContext.isLoggedIn = Boolean(authToken && loginToken);

    const [{ featureFlags, isSiteWideJackpotEnabled }, experiments] = await Promise.all([
        getFeatureFlags({ currentRoute }),
        getExperiments({ currentRoute })
    ]);
    serverContext.featureFlags = featureFlags;
    serverContext.isSiteWideJackpotEnabled = isSiteWideJackpotEnabled;
    serverContext.experiments = experiments;
    const isGameWindowV3Enabled = isExperimentEnabled(experiments, AMPLITUDE_EXPERIMENTS.GAME_WINDOW_V3);
    serverContext.launcherUrlResolution = {
        shouldUseSameDomainLauncher:
            !serverContext.isAndroidWrapper &&
            !serverContext.isIosWrapper &&
            isExperimentEnabled(experiments, AMPLITUDE_EXPERIMENTS.SAME_DOMAIN_LAUNCH),
        isGameWindowV3Enabled,
        gamePlayPath: isGameWindowV3Enabled ? GAME_WINDOW_PLAY_PATH_PREFIX : GAME_PLAY_PATH_PREFIX
    };
    serverContext.swjTickerRedesignVariant = getExperimentValue(experiments, AMPLITUDE_EXPERIMENTS.SWJ_TICKER_REDESIGN);
});
